import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Search, Filter, Eye, FileText, Download, 
  CheckCircle, XCircle, Truck, RotateCcw, Clock, Camera, Pen, Upload
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import VehicleConditionManager from '../../components/admin/VehicleConditionManager';
import InteractiveReportGenerator from '../../components/admin/InteractiveReportGenerator';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminBookings = () => {
  const { token } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');  // all, upcoming_pickups, upcoming_returns, in_progress, past
  const [vehicleFilter, setVehicleFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState(null);
  const [locations, setLocations] = useState([]);
  
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [conditionDialogOpen, setConditionDialogOpen] = useState(false);
  const [interactiveReportOpen, setInteractiveReportOpen] = useState(false);
  const [interactiveReportType, setInteractiveReportType] = useState('delivery');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [reportType, setReportType] = useState('delivery');
  const [reportForm, setReportForm] = useState({
    mileage: '',
    fuel_level: 'full',
    condition: 'excellent',
    damages: '',
    extra_charges: 0,
    notes: ''
  });
  
  // New booking dialog state
  const [newBookingDialogOpen, setNewBookingDialogOpen] = useState(false);
  const [newBookingLoading, setNewBookingLoading] = useState(false);
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [searchingVehicles, setSearchingVehicles] = useState(false);
  const [selectedVehicleDetails, setSelectedVehicleDetails] = useState(null);
  const [customPrice, setCustomPrice] = useState('');
  const [useCustomPrice, setUseCustomPrice] = useState(false);
  const [newBookingForm, setNewBookingForm] = useState({
    vehicle_id: '',
    pickup_location_id: '',
    return_location_id: '',
    pickup_date: '',
    pickup_time: '10:00',
    return_date: '',
    return_time: '10:00',
    customer_full_name: '',
    customer_email: '',
    customer_phone: '',
    customer_street: '',
    customer_postal_code: '',
    customer_city: '',
    customer_country: 'Switzerland',
    customer_date_of_birth: '',
    notes: '',
    language: 'fr',
    custom_price: null
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchBookings = async () => {
    try {
      const params = {};
      if (filterType !== 'all') params.filter_type = filterType;
      if (vehicleFilter !== 'all') params.vehicle_id = vehicleFilter;
      if (searchTerm) params.search = searchTerm;
      
      const response = await axios.get(`${API_URL}/api/admin/bookings`, { headers, params });
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/vehicles`, { headers });
      setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/bookings/stats`, { headers });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Search available vehicles for selected dates
  const searchAvailableVehicles = async () => {
    if (!newBookingForm.pickup_date || !newBookingForm.return_date) {
      toast.error('Veuillez sélectionner les dates de location');
      return;
    }

    setSearchingVehicles(true);
    setAvailableVehicles([]);
    setSelectedVehicleDetails(null);
    setNewBookingForm(prev => ({ ...prev, vehicle_id: '' }));

    try {
      const response = await axios.get(`${API_URL}/api/vehicles/available`, {
        params: {
          pickup_date: newBookingForm.pickup_date,
          return_date: newBookingForm.return_date
        }
      });
      setAvailableVehicles(response.data);
      if (response.data.length === 0) {
        toast.info('Aucun véhicule disponible pour ces dates');
      }
    } catch (error) {
      console.error('Error searching vehicles:', error);
      toast.error('Erreur lors de la recherche des véhicules');
    } finally {
      setSearchingVehicles(false);
    }
  };

  // Calculate price when vehicle is selected
  const handleVehicleSelect = (vehicleId) => {
    const vehicle = availableVehicles.find(v => v.id === vehicleId);
    if (vehicle) {
      setSelectedVehicleDetails(vehicle);
      setNewBookingForm(prev => ({ ...prev, vehicle_id: vehicleId }));
      
      // Calculate rental days
      const pickup = new Date(newBookingForm.pickup_date);
      const returnDate = new Date(newBookingForm.return_date);
      const days = Math.max(1, Math.ceil((returnDate - pickup) / (1000 * 60 * 60 * 24)));
      
      // Calculate price using tiers
      let dailyRate = vehicle.base_rate || vehicle.price_per_day || 89;
      const tiers = vehicle.pricing_tiers || [];
      for (const tier of tiers.sort((a, b) => a.min_days - b.min_days)) {
        if (days >= tier.min_days && days <= tier.max_days) {
          dailyRate = tier.daily_rate;
          break;
        }
      }
      
      const calculatedPrice = dailyRate * days;
      setCustomPrice(calculatedPrice.toString());
      setUseCustomPrice(false);
    }
  };

  const handleCreateBookingWithPaymentLink = async () => {
    // Validate required fields
    if (!newBookingForm.vehicle_id || !newBookingForm.pickup_date || !newBookingForm.return_date || 
        !newBookingForm.customer_full_name || !newBookingForm.customer_email) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setNewBookingLoading(true);
    try {
      // Add custom price to the form data
      const bookingData = {
        ...newBookingForm,
        custom_price: useCustomPrice ? parseFloat(customPrice) : null
      };

      const response = await axios.post(
        `${API_URL}/api/admin/bookings/with-payment-link`,
        bookingData,
        { headers }
      );
      
      toast.success(`Lien de paiement envoyé à ${newBookingForm.customer_email}`);
      setNewBookingDialogOpen(false);
      // Reset all states
      setNewBookingForm({
        vehicle_id: '',
        pickup_location_id: '',
        return_location_id: '',
        pickup_date: '',
        pickup_time: '10:00',
        return_date: '',
        return_time: '10:00',
        customer_full_name: '',
        customer_email: '',
        customer_phone: '',
        customer_street: '',
        customer_postal_code: '',
        customer_city: '',
        customer_country: 'Switzerland',
        customer_date_of_birth: '',
        notes: '',
        language: 'fr',
        custom_price: null
      });
      setAvailableVehicles([]);
      setSelectedVehicleDetails(null);
      setCustomPrice('');
      setUseCustomPrice(false);
      // Refresh bookings
      fetchBookings();
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de la réservation');
    } finally {
      setNewBookingLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
    fetchLocations();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [filterType, vehicleFilter, searchTerm]);

  const handleDownloadContract = async (bookingId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/bookings/${bookingId}/contract`,
        { headers, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Contract_${bookingId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Contract downloaded');
    } catch (error) {
      console.error('Error downloading contract:', error);
      toast.error('Failed to download contract');
    }
  };

  const handleDownloadInvoice = async (bookingId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/bookings/${bookingId}/invoice`,
        { headers, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${bookingId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Failed to download invoice');
    }
  };

  const handleDownloadDeliveryReport = async (bookingId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/bookings/${bookingId}/delivery-report`,
        { headers, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PV_Livraison_${bookingId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Delivery Report downloaded');
    } catch (error) {
      console.error('Error downloading delivery report:', error);
      toast.error('Failed to download delivery report');
    }
  };

  const handleDownloadReturnReport = async (bookingId) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/bookings/${bookingId}/return-report`,
        { headers, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PV_Restitution_${bookingId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Return Report downloaded');
    } catch (error) {
      console.error('Error downloading return report:', error);
      toast.error('Failed to download return report');
    }
  };

  const handleStatusUpdate = async (bookingId, newStatus) => {
    try {
      await axios.put(`${API_URL}/api/admin/bookings/${bookingId}?status=${newStatus}`, null, {
        headers
      });
      toast.success(`Booking status updated to ${newStatus}`);
      fetchBookings();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleReportSubmit = async () => {
    if (!selectedBooking) return;

    try {
      const endpoint = reportType === 'delivery' 
        ? `/api/admin/bookings/${selectedBooking.id}/delivery-report`
        : `/api/admin/bookings/${selectedBooking.id}/return-report`;

      await axios.post(`${API_URL}${endpoint}`, null, {
        headers,
        params: {
          mileage: parseInt(reportForm.mileage),
          fuel_level: reportForm.fuel_level,
          condition: reportForm.condition,
          damages: reportForm.damages || null,
          extra_charges: parseFloat(reportForm.extra_charges) || 0,
          notes: reportForm.notes || null
        }
      });

      toast.success(`${reportType === 'delivery' ? 'Delivery' : 'Return'} report created`);
      setReportDialogOpen(false);
      setReportForm({ mileage: '', fuel_level: 'full', condition: 'excellent', damages: '', extra_charges: 0, notes: '' });
      fetchBookings();
    } catch (error) {
      console.error('Error creating report:', error);
      toast.error('Failed to create report');
    }
  };

  const downloadReport = async (bookingId, type) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/bookings/${bookingId}/report-pdf/${type}`,
        { headers, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_report_${bookingId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error('Failed to download report');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      confirmed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      delivered: 'bg-green-500/20 text-green-400 border-green-500/30',
      returned: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: Clock,
      confirmed: CheckCircle,
      delivered: Truck,
      returned: RotateCcw,
      cancelled: XCircle
    };
    return icons[status] || Clock;
  };

  // Bookings are now filtered server-side
  const filteredBookings = bookings;

  return (
    <div className="space-y-6" data-testid="admin-bookings">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-serif text-3xl text-[#181B1F] font-light">Bookings</h1>
          <p className="text-gray-500 mt-1">Manage reservations, deliveries, and returns</p>
        </div>
        <Button
          onClick={() => setNewBookingDialogOpen(true)}
          className="btn-primary"
          data-testid="new-booking-btn"
        >
          <FileText className="w-4 h-4 mr-2" />
          Nouvelle réservation
        </Button>
      </div>

      {/* Quick Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div 
            className={`bg-white border border-gray-100 rounded-sm p-4 cursor-pointer transition-all ${filterType === 'upcoming_pickups' ? 'ring-2 ring-primary' : 'hover:border-gray-300'}`}
            onClick={() => setFilterType(filterType === 'upcoming_pickups' ? 'all' : 'upcoming_pickups')}
          >
            <p className="text-gray-500 text-xs uppercase tracking-wider">Upcoming Pickups</p>
            <p className="text-2xl text-[#181B1F] mt-1">{stats.upcoming_pickups}</p>
            {stats.todays_pickups > 0 && (
              <p className="text-green-400 text-xs mt-1">{stats.todays_pickups} today</p>
            )}
          </div>
          <div 
            className={`bg-white border border-gray-100 rounded-sm p-4 cursor-pointer transition-all ${filterType === 'upcoming_returns' ? 'ring-2 ring-primary' : 'hover:border-gray-300'}`}
            onClick={() => setFilterType(filterType === 'upcoming_returns' ? 'all' : 'upcoming_returns')}
          >
            <p className="text-gray-500 text-xs uppercase tracking-wider">Upcoming Returns</p>
            <p className="text-2xl text-[#181B1F] mt-1">{stats.upcoming_returns}</p>
            {stats.todays_returns > 0 && (
              <p className="text-yellow-400 text-xs mt-1">{stats.todays_returns} today</p>
            )}
          </div>
          <div 
            className={`bg-white border border-gray-100 rounded-sm p-4 cursor-pointer transition-all ${filterType === 'in_progress' ? 'ring-2 ring-primary' : 'hover:border-gray-300'}`}
            onClick={() => setFilterType(filterType === 'in_progress' ? 'all' : 'in_progress')}
          >
            <p className="text-gray-500 text-xs uppercase tracking-wider">In Progress</p>
            <p className="text-2xl text-[#181B1F] mt-1">{stats.in_progress}</p>
          </div>
          <div 
            className={`bg-white border border-gray-100 rounded-sm p-4 cursor-pointer transition-all ${filterType === 'past' ? 'ring-2 ring-primary' : 'hover:border-gray-300'}`}
            onClick={() => setFilterType(filterType === 'past' ? 'all' : 'past')}
          >
            <p className="text-gray-500 text-xs uppercase tracking-wider">Past Rentals</p>
            <p className="text-2xl text-gray-500 mt-1">View</p>
          </div>
          <div 
            className={`bg-white border border-gray-100 rounded-sm p-4 cursor-pointer transition-all ${filterType === 'all' ? 'ring-2 ring-primary' : 'hover:border-gray-300'}`}
            onClick={() => setFilterType('all')}
          >
            <p className="text-gray-500 text-xs uppercase tracking-wider">All Bookings</p>
            <p className="text-2xl text-[#181B1F] mt-1">{bookings.length}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4 flex-wrap items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by customer, vehicle, email, or booking ID..."
            className="input-default pl-10"
            data-testid="search-bookings"
          />
        </div>

        {/* Vehicle Filter */}
        <Select value={vehicleFilter} onValueChange={setVehicleFilter}>
          <SelectTrigger className="input-default w-48" data-testid="filter-vehicle">
            <SelectValue placeholder="All Vehicles" />
          </SelectTrigger>
          <SelectContent className="bg-white border-gray-200">
            <SelectItem value="all" className="text-[#181B1F]">All Vehicles</SelectItem>
            {vehicles.map((v) => (
              <SelectItem key={v.id} value={v.id} className="text-[#181B1F]">
                {v.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Clear Filters */}
        {(filterType !== 'all' || vehicleFilter !== 'all' || searchTerm) && (
          <Button
            variant="outline"
            className="btn-outline text-sm"
            onClick={() => {
              setFilterType('all');
              setVehicleFilter('all');
              setSearchTerm('');
            }}
            data-testid="clear-filters"
          >
            Clear Filters
          </Button>
        )}

        {/* Current filter indicator */}
        {filterType !== 'all' && (
          <Badge className="bg-primary/20 text-primary border-primary/30">
            {filterType.replace(/_/g, ' ')}
          </Badge>
        )}
      </div>

      {/* Bookings Table */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Loading bookings...</p>
        </div>
      ) : filteredBookings.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">No bookings found</p>
        </div>
      ) : (
        <div className="border border-gray-200 rounded-sm overflow-hidden">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow className="border-gray-200 hover:bg-transparent">
                <TableHead className="text-gray-500">Booking ID</TableHead>
                <TableHead className="text-gray-500">Vehicle</TableHead>
                <TableHead className="text-gray-500">Customer</TableHead>
                <TableHead className="text-gray-500">Dates</TableHead>
                <TableHead className="text-gray-500">Total</TableHead>
                <TableHead className="text-gray-500">Status</TableHead>
                <TableHead className="text-gray-500 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredBookings.map((booking) => {
                const StatusIcon = getStatusIcon(booking.status);
                return (
                  <TableRow key={booking.id} className="border-gray-100 hover:bg-gray-50">
                    <TableCell className="font-mono text-gray-500 text-sm">
                      {booking.id.slice(0, 8)}...
                    </TableCell>
                    <TableCell className="text-[#181B1F]">{booking.vehicle_name}</TableCell>
                    <TableCell className="text-white/80">
                      {booking.customer_info?.full_name}
                      <br />
                      <span className="text-gray-400 text-sm">{booking.customer_info?.email}</span>
                    </TableCell>
                    <TableCell className="text-gray-500 text-sm">
                      {booking.pickup_date} - {booking.return_date}
                      <br />
                      <span className="text-gray-400">{booking.rental_days} days</span>
                    </TableCell>
                    <TableCell className="text-[#181B1F] font-medium">
                      CHF {booking.total_price?.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <Badge className={`${getStatusColor(booking.status)} border`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {booking.status}
                        </Badge>
                        {booking.is_internal_booking && (
                          <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 border text-xs">
                            Interne
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {/* View Details */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelectedBooking(booking);
                            setDetailsDialogOpen(true);
                          }}
                          className="text-gray-500 hover:text-[#181B1F]"
                          title="View Details"
                          data-testid={`view-booking-${booking.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        
                        {/* Download Contract */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDownloadContract(booking.id)}
                          className="text-primary hover:text-primary/80"
                          title="Download Contract"
                          data-testid={`download-contract-${booking.id}`}
                        >
                          <FileText className="w-4 h-4" />
                        </Button>
                        
                        {/* Download Invoice */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDownloadInvoice(booking.id)}
                          className="text-yellow-400 hover:text-yellow-300"
                          title="Download Invoice"
                          data-testid={`download-invoice-${booking.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        
                        {/* Delivery Report */}
                        {booking.status === 'confirmed' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setSelectedBooking(booking);
                              setInteractiveReportType('delivery');
                              setInteractiveReportOpen(true);
                            }}
                            className="text-green-400 hover:text-green-300"
                            title="Create Delivery Report"
                            data-testid={`create-delivery-report-${booking.id}`}
                          >
                            <Pen className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {/* Return Report */}
                        {booking.status === 'delivered' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setSelectedBooking(booking);
                              setInteractiveReportType('return');
                              setInteractiveReportOpen(true);
                            }}
                            className="text-blue-400 hover:text-blue-300"
                            title="Create Return Report"
                            data-testid={`create-return-report-${booking.id}`}
                          >
                            <Pen className="w-4 h-4" />
                          </Button>
                        )}
                        {booking.delivery_report && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => downloadReport(booking.id, 'delivery')}
                            className="text-gray-500 hover:text-[#181B1F]"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Booking Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#181B1F]">
              Booking Details
            </DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-6">
              {/* Status Update */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-sm">
                <div className="flex items-center gap-3">
                  <Badge className={`${getStatusColor(selectedBooking.status)} border`}>
                    {selectedBooking.status}
                  </Badge>
                  {selectedBooking.is_internal_booking && (
                    <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 border">
                      Réservation Interne ({selectedBooking.internal_booking_type || 'N/A'})
                    </Badge>
                  )}
                  <span className="text-gray-400 text-sm">
                    ID: {selectedBooking.id}
                  </span>
                </div>
                <Select 
                  value={selectedBooking.status} 
                  onValueChange={(v) => handleStatusUpdate(selectedBooking.id, v)}
                >
                  <SelectTrigger className="input-default w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="pending" className="text-[#181B1F]">Pending</SelectItem>
                    <SelectItem value="confirmed" className="text-[#181B1F]">Confirmed</SelectItem>
                    <SelectItem value="delivered" className="text-[#181B1F]">Delivered</SelectItem>
                    <SelectItem value="returned" className="text-[#181B1F]">Returned</SelectItem>
                    <SelectItem value="cancelled" className="text-[#181B1F]">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Vehicle & Customer */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h4 className="text-gray-400 text-sm uppercase tracking-wider">Vehicle</h4>
                  <p className="text-[#181B1F]">{selectedBooking.vehicle_name}</p>
                  <p className="text-gray-500 text-sm">
                    {selectedBooking.pickup_date} - {selectedBooking.return_date}
                  </p>
                  <p className="text-gray-500 text-sm">
                    {selectedBooking.rental_days} days @ CHF {selectedBooking.daily_rate}/day
                  </p>
                </div>
                <div className="space-y-3">
                  <h4 className="text-gray-400 text-sm uppercase tracking-wider">Customer</h4>
                  <p className="text-[#181B1F]">{selectedBooking.customer_info?.full_name}</p>
                  <p className="text-gray-500 text-sm">{selectedBooking.customer_info?.email}</p>
                  <p className="text-gray-500 text-sm">{selectedBooking.customer_info?.phone}</p>
                </div>
              </div>

              {/* Locations */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="text-gray-400 text-sm uppercase tracking-wider mb-2">Pickup</h4>
                  <p className="text-white/80">{selectedBooking.pickup_location_name}</p>
                </div>
                <div>
                  <h4 className="text-gray-400 text-sm uppercase tracking-wider mb-2">Return</h4>
                  <p className="text-white/80">{selectedBooking.return_location_name}</p>
                </div>
              </div>

              {/* Documents */}
              <div>
                <h4 className="text-gray-400 text-sm uppercase tracking-wider mb-2">Documents</h4>
                <div className="flex flex-wrap gap-4 items-center">
                  {/* ID Document */}
                  {selectedBooking.customer_info?.id_document_url ? (
                    <a 
                      href={`${API_URL}${selectedBooking.customer_info.id_document_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#4A945D] hover:underline text-sm flex items-center gap-1"
                    >
                      <FileText className="w-4 h-4" />
                      View ID Document
                    </a>
                  ) : (
                    <label className="text-sm text-gray-400 cursor-pointer hover:text-[#4A945D] flex items-center gap-1">
                      <Upload className="w-4 h-4" />
                      Upload ID
                      <input 
                        type="file" 
                        className="hidden" 
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const formData = new FormData();
                          formData.append('file', file);
                          formData.append('document_type', 'id');
                          try {
                            await axios.post(`${API_URL}/api/admin/bookings/${selectedBooking.id}/documents`, formData, {
                              headers: { ...headers, 'Content-Type': 'multipart/form-data' }
                            });
                            toast.success('ID document uploaded');
                            fetchBookings();
                            setSelectedBooking(prev => ({...prev, customer_info: {...prev.customer_info, id_document_url: 'uploaded'}}));
                          } catch (err) {
                            toast.error('Upload failed');
                          }
                        }}
                      />
                    </label>
                  )}
                  
                  {/* License Document */}
                  {selectedBooking.customer_info?.license_document_url ? (
                    <a 
                      href={`${API_URL}${selectedBooking.customer_info.license_document_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#4A945D] hover:underline text-sm flex items-center gap-1"
                    >
                      <FileText className="w-4 h-4" />
                      View License
                    </a>
                  ) : (
                    <label className="text-sm text-gray-400 cursor-pointer hover:text-[#4A945D] flex items-center gap-1">
                      <Upload className="w-4 h-4" />
                      Upload License
                      <input 
                        type="file" 
                        className="hidden" 
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const formData = new FormData();
                          formData.append('file', file);
                          formData.append('document_type', 'license');
                          try {
                            await axios.post(`${API_URL}/api/admin/bookings/${selectedBooking.id}/documents`, formData, {
                              headers: { ...headers, 'Content-Type': 'multipart/form-data' }
                            });
                            toast.success('License document uploaded');
                            fetchBookings();
                            setSelectedBooking(prev => ({...prev, customer_info: {...prev.customer_info, license_document_url: 'uploaded'}}));
                          } catch (err) {
                            toast.error('Upload failed');
                          }
                        }}
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Vehicle Condition Button */}
              <div>
                <h4 className="text-gray-400 text-sm uppercase tracking-wider mb-2">Vehicle Condition</h4>
                <Button
                  onClick={() => {
                    setDetailsDialogOpen(false);
                    setConditionDialogOpen(true);
                  }}
                  className="btn-outline"
                  data-testid="open-condition-manager"
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Manage Condition & Photos
                </Button>
              </div>

              {/* Total */}
              <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                <span className="text-gray-500">Total Amount</span>
                <span className="text-2xl text-[#181B1F] font-light">
                  CHF {selectedBooking.total_price?.toFixed(2)}
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Vehicle Condition Manager Dialog */}
      <Dialog open={conditionDialogOpen} onOpenChange={setConditionDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#181B1F]">
              Vehicle Condition Report
            </DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <VehicleConditionManager
              booking={selectedBooking}
              onClose={() => setConditionDialogOpen(false)}
              onUpdate={() => fetchBookings()}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Report Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#181B1F]">
              {reportType === 'delivery' ? 'Delivery Report' : 'Return Report'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-500">Mileage (km) *</Label>
              <Input
                type="number"
                value={reportForm.mileage}
                onChange={(e) => setReportForm(prev => ({ ...prev, mileage: e.target.value }))}
                className="input-default"
                placeholder="Current mileage"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-500">Fuel Level</Label>
                <Select 
                  value={reportForm.fuel_level} 
                  onValueChange={(v) => setReportForm(prev => ({ ...prev, fuel_level: v }))}
                >
                  <SelectTrigger className="input-default">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="full" className="text-[#181B1F]">Full</SelectItem>
                    <SelectItem value="3/4" className="text-[#181B1F]">3/4</SelectItem>
                    <SelectItem value="1/2" className="text-[#181B1F]">1/2</SelectItem>
                    <SelectItem value="1/4" className="text-[#181B1F]">1/4</SelectItem>
                    <SelectItem value="empty" className="text-[#181B1F]">Empty</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-500">Condition</Label>
                <Select 
                  value={reportForm.condition} 
                  onValueChange={(v) => setReportForm(prev => ({ ...prev, condition: v }))}
                >
                  <SelectTrigger className="input-default">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    <SelectItem value="excellent" className="text-[#181B1F]">Excellent</SelectItem>
                    <SelectItem value="good" className="text-[#181B1F]">Good</SelectItem>
                    <SelectItem value="fair" className="text-[#181B1F]">Fair</SelectItem>
                    <SelectItem value="poor" className="text-[#181B1F]">Poor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {reportType === 'return' && (
              <>
                <div className="space-y-2">
                  <Label className="text-gray-500">Damages (if any)</Label>
                  <Textarea
                    value={reportForm.damages}
                    onChange={(e) => setReportForm(prev => ({ ...prev, damages: e.target.value }))}
                    className="input-default"
                    placeholder="Describe any damages..."
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-500">Extra Charges (CHF)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={reportForm.extra_charges}
                    onChange={(e) => setReportForm(prev => ({ ...prev, extra_charges: e.target.value }))}
                    className="input-default"
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label className="text-gray-500">Notes</Label>
              <Textarea
                value={reportForm.notes}
                onChange={(e) => setReportForm(prev => ({ ...prev, notes: e.target.value }))}
                className="input-default"
                placeholder="Additional notes..."
              />
            </div>

            <div className="flex justify-end gap-4 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setReportDialogOpen(false)}
                className="btn-outline"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleReportSubmit}
                className="btn-primary"
                disabled={!reportForm.mileage}
              >
                Create Report
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Interactive Report Generator Dialog */}
      <Dialog open={interactiveReportOpen} onOpenChange={setInteractiveReportOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-5xl max-h-[90vh] overflow-y-auto">
          {selectedBooking && (
            <InteractiveReportGenerator
              booking={selectedBooking}
              reportType={interactiveReportType}
              onClose={() => {
                setInteractiveReportOpen(false);
                setSelectedBooking(null);
              }}
              onComplete={() => {
                fetchBookings();
                fetchStats();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* New Booking with Payment Link Dialog */}
      <Dialog open={newBookingDialogOpen} onOpenChange={setNewBookingDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[#181B1F]">
              Nouvelle réservation client
            </DialogTitle>
            <p className="text-gray-500 text-sm">
              Créez une réservation et envoyez un lien de paiement Stripe au client par email
            </p>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Step 1: Dates & Search */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-4">
              <h3 className="font-medium text-[#181B1F] flex items-center gap-2">
                <span className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm">1</span>
                Période de location
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Date de prise en charge *</Label>
                  <Input
                    type="date"
                    value={newBookingForm.pickup_date}
                    onChange={(e) => {
                      setNewBookingForm({...newBookingForm, pickup_date: e.target.value, vehicle_id: ''});
                      setAvailableVehicles([]);
                      setSelectedVehicleDetails(null);
                    }}
                    className="input-default"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Heure</Label>
                  <Input
                    type="time"
                    value={newBookingForm.pickup_time}
                    onChange={(e) => setNewBookingForm({...newBookingForm, pickup_time: e.target.value})}
                    className="input-default"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Date de retour *</Label>
                  <Input
                    type="date"
                    value={newBookingForm.return_date}
                    onChange={(e) => {
                      setNewBookingForm({...newBookingForm, return_date: e.target.value, vehicle_id: ''});
                      setAvailableVehicles([]);
                      setSelectedVehicleDetails(null);
                    }}
                    className="input-default"
                    min={newBookingForm.pickup_date || new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Heure</Label>
                  <Input
                    type="time"
                    value={newBookingForm.return_time}
                    onChange={(e) => setNewBookingForm({...newBookingForm, return_time: e.target.value})}
                    className="input-default"
                  />
                </div>
              </div>

              <Button
                onClick={searchAvailableVehicles}
                disabled={!newBookingForm.pickup_date || !newBookingForm.return_date || searchingVehicles}
                className="w-full"
                variant="outline"
              >
                {searchingVehicles ? 'Recherche...' : 'Rechercher les véhicules disponibles'}
              </Button>
            </div>

            {/* Step 2: Vehicle Selection */}
            {availableVehicles.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <h3 className="font-medium text-[#181B1F] flex items-center gap-2">
                  <span className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm">2</span>
                  Sélectionner un véhicule ({availableVehicles.length} disponible{availableVehicles.length > 1 ? 's' : ''})
                </h3>
                
                <div className="grid gap-3 max-h-60 overflow-y-auto">
                  {availableVehicles.map((v) => {
                    const days = Math.max(1, Math.ceil((new Date(newBookingForm.return_date) - new Date(newBookingForm.pickup_date)) / (1000 * 60 * 60 * 24)));
                    let dailyRate = v.base_rate || v.price_per_day || 89;
                    const tiers = v.pricing_tiers || [];
                    for (const tier of tiers.sort((a, b) => a.min_days - b.min_days)) {
                      if (days >= tier.min_days && days <= tier.max_days) {
                        dailyRate = tier.daily_rate;
                        break;
                      }
                    }
                    const totalPrice = dailyRate * days;
                    
                    return (
                      <div
                        key={v.id}
                        onClick={() => handleVehicleSelect(v.id)}
                        className={`p-3 border rounded-lg cursor-pointer transition-all ${
                          newBookingForm.vehicle_id === v.id 
                            ? 'border-primary bg-primary/5' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium text-[#181B1F]">{v.name}</p>
                            <p className="text-sm text-gray-500">{v.category} • {v.transmission}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-primary">CHF {totalPrice.toFixed(0)}</p>
                            <p className="text-xs text-gray-500">{days} jour{days > 1 ? 's' : ''} × CHF {dailyRate}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 3: Price & Locations */}
            {selectedVehicleDetails && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <h3 className="font-medium text-[#181B1F] flex items-center gap-2">
                  <span className="w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-sm">3</span>
                  Prix et lieux
                </h3>

                {/* Custom Price */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-gray-700">Prix de la location (CHF)</Label>
                    <label className="flex items-center gap-2 text-sm text-gray-500">
                      <input
                        type="checkbox"
                        checked={useCustomPrice}
                        onChange={(e) => setUseCustomPrice(e.target.checked)}
                        className="rounded"
                      />
                      Prix négocié
                    </label>
                  </div>
                  <Input
                    type="number"
                    value={customPrice}
                    onChange={(e) => {
                      setCustomPrice(e.target.value);
                      setUseCustomPrice(true);
                    }}
                    className={`input-default ${useCustomPrice ? 'border-primary' : ''}`}
                    placeholder="Prix en CHF"
                  />
                  {useCustomPrice && (
                    <p className="text-xs text-amber-600">
                      ⚠️ Prix personnalisé - différent du tarif standard
                    </p>
                  )}
                </div>

                {/* Locations */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-gray-700">Lieu de prise en charge</Label>
                    <Select 
                      value={newBookingForm.pickup_location_id} 
                      onValueChange={(v) => setNewBookingForm({...newBookingForm, pickup_location_id: v})}
                    >
                      <SelectTrigger className="input-default">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {locations.map((loc) => (
                          <SelectItem key={loc.id} value={loc.id} className="text-[#181B1F]">
                            {loc.name_fr || loc.name_en}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-700">Lieu de retour</Label>
                    <Select 
                      value={newBookingForm.return_location_id} 
                      onValueChange={(v) => setNewBookingForm({...newBookingForm, return_location_id: v})}
                    >
                      <SelectTrigger className="input-default">
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {locations.map((loc) => (
                          <SelectItem key={loc.id} value={loc.id} className="text-[#181B1F]">
                            {loc.name_fr || loc.name_en}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Customer Info */}
            {selectedVehicleDetails && (
              <>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Nom complet *</Label>
                <Input
                  value={newBookingForm.customer_full_name}
                  onChange={(e) => setNewBookingForm({...newBookingForm, customer_full_name: e.target.value})}
                  placeholder="Jean Dupont"
                  className="input-default"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Email *</Label>
                <Input
                  type="email"
                  value={newBookingForm.customer_email}
                  onChange={(e) => setNewBookingForm({...newBookingForm, customer_email: e.target.value})}
                  placeholder="client@email.com"
                  className="input-default"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Téléphone</Label>
                <Input
                  value={newBookingForm.customer_phone}
                  onChange={(e) => setNewBookingForm({...newBookingForm, customer_phone: e.target.value})}
                  placeholder="+41 79 123 45 67"
                  className="input-default"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Date de naissance</Label>
                <Input
                  type="date"
                  value={newBookingForm.customer_date_of_birth}
                  onChange={(e) => setNewBookingForm({...newBookingForm, customer_date_of_birth: e.target.value})}
                  className="input-default"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-700">Adresse</Label>
              <Input
                value={newBookingForm.customer_street}
                onChange={(e) => setNewBookingForm({...newBookingForm, customer_street: e.target.value})}
                placeholder="Rue du Mont-Blanc 22"
                className="input-default"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Code postal</Label>
                <Input
                  value={newBookingForm.customer_postal_code}
                  onChange={(e) => setNewBookingForm({...newBookingForm, customer_postal_code: e.target.value})}
                  placeholder="1201"
                  className="input-default"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Ville</Label>
                <Input
                  value={newBookingForm.customer_city}
                  onChange={(e) => setNewBookingForm({...newBookingForm, customer_city: e.target.value})}
                  placeholder="Genève"
                  className="input-default"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Langue</Label>
                <Select 
                  value={newBookingForm.language} 
                  onValueChange={(v) => setNewBookingForm({...newBookingForm, language: v})}
                >
                  <SelectTrigger className="input-default">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="fr" className="text-[#181B1F]">Français</SelectItem>
                    <SelectItem value="en" className="text-[#181B1F]">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-700">Notes internes</Label>
              <Textarea
                value={newBookingForm.notes}
                onChange={(e) => setNewBookingForm({...newBookingForm, notes: e.target.value})}
                placeholder="Notes pour cette réservation..."
                className="input-default"
                rows={2}
              />
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <p className="text-blue-800 text-sm">
                <strong>Comment ça fonctionne :</strong><br />
                1. Un email avec le lien de paiement Stripe sera envoyé au client<br />
                2. Le client a 24h pour effectuer le paiement<br />
                3. Une fois payé, la réservation est confirmée automatiquement<br />
                4. La facture est créée dans Bexio et marquée comme payée
              </p>
            </div>
              </>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={() => setNewBookingDialogOpen(false)}
            >
              Annuler
            </Button>
            <Button
              onClick={handleCreateBookingWithPaymentLink}
              disabled={newBookingLoading}
              className="btn-primary"
            >
              {newBookingLoading ? 'Création...' : 'Créer et envoyer le lien'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBookings;
